import { formatCurrency } from '@if/core/utils'
import {
  Banner,
  ControlledCheckboxGroup,
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  ControlledTextField,
  FieldGrid,
  FormMethods,
  useFormDialog,
} from '@if/mui'
import { Skeleton, Stack } from '@mui/material'
import { useComputeCalendarAction, useEnsureRefundableProducts, useGetRefundableProducts } from '@src/api/loan/loan-api'
import { getToday } from '@src/services/DateTimeProvider'
import { EPaymentMethod, useTranslatedProductRefundPaymentMethods } from '@src/types/constants'
import { ECalendarSimulationAction, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { Undo2 } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getProductBalance, getProductBalancesAt } from './LoanCalendarProductRefund-selector'
import { ProductRefundSchemaType, useProductRefundSchema } from './LoanCalendarProductRefund-schema'

interface LoanCalendarProductRefundBodyProps {
  form: FormMethods<ProductRefundSchemaType>
  initialSignedSimulation: SignedCalendarSimulation
  disabled: boolean
}

const LoanCalendarProductRefundBody = ({
  form,
  initialSignedSimulation,
  disabled,
}: LoanCalendarProductRefundBodyProps) => {
  const { t } = useTranslation()
  const { control, setValue } = form
  const { loan } = initialSignedSimulation.simulation.fullLoan
  const { operationInformations } = initialSignedSimulation.simulation
  const paymentMethodOptions = useTranslatedProductRefundPaymentMethods()
  // Read here, inside the dialog: the dialog does not re-render its slots when the declaring
  // component does, so a list fetched at the declaration site would never reach an open dialog.
  const {
    data: refundableProducts = [],
    isPending: refundableProductsPending,
    error: refundableProductsError,
  } = useGetRefundableProducts(loan.id, loan.financingCompanyId)
  // A failed fetch leaves the list empty too; the warning is for a loan that truly funds nothing refundable.
  // The failure itself is already surfaced by the form: its default values await the same list.
  const hasNoRefundableProduct = !refundableProductsPending && !refundableProductsError && refundableProducts.length === 0

  const effectiveDate = useWatch({ control, name: 'effectiveDate' })
  const paymentMethodId = useWatch({ control, name: 'paymentMethodId' })

  const isCheque = paymentMethodId === EPaymentMethod.Cheque

  // The balances shown are the ones the backend will apply the refund against: as of the refund
  // date. While the date precedes every operation (it is still being typed), the options name
  // the product alone rather than claim a zero balance.
  const productBalances = useMemo(
    () => getProductBalancesAt(operationInformations, effectiveDate ?? getToday()),
    [operationInformations, effectiveDate],
  )
  const productOptions = useMemo(
    () =>
      refundableProducts.map((product) => ({
        value: product.productId,
        label: productBalances
          ? t('loan.payment.productRefundDialog.productOption', {
              product: t(`enum.product.${product.productId}`),
              balance: formatCurrency(getProductBalance(productBalances, product.productId)),
            })
          : t(`enum.product.${product.productId}`),
      })),
    [refundableProducts, productBalances, t],
  )

  // A cheque number only means something on a cheque; a method change drops one already typed.
  const handlePaymentMethodChange = useCallback(() => {
    setValue('chequeNumber', null, { shouldValidate: true })
  }, [setValue])

  return (
    <Stack spacing={2}>
      {refundableProductsPending && (
        // Sized to the checkbox group so the form does not jump once the list lands.
        <Skeleton variant="rounded" height={72} />
      )}
      {hasNoRefundableProduct && (
        <Banner severity="warning">{t('loan.payment.productRefundDialog.noRefundableProduct')}</Banner>
      )}
      {refundableProducts.length > 0 && (
        <ControlledCheckboxGroup
          control={control}
          name="productIds"
          options={productOptions}
          label={t('loan.payment.productRefundDialog.products')}
          disabled={disabled}
        />
      )}

      <FieldGrid columns={{ xs: 1, sm: 2 }}>
        <ControlledNumberField
          control={control}
          name="amount"
          isMoney
          label={t('loan.payment.productRefundDialog.amount')}
          disabled={disabled}
        />
        <ControlledDateOnlyPicker
          control={control}
          name="effectiveDate"
          label={t('loan.payment.productRefundDialog.effectiveDate')}
          // The money is already in hand: the refund is dated between the activation and today.
          minDate={loan.activationOn}
          maxDate={getToday()}
          disabled={disabled}
        />
        <ControlledSelect
          control={control}
          name="paymentMethodId"
          options={paymentMethodOptions}
          label={t('loan.payment.productRefundDialog.paymentMethod')}
          disableEmptyOption
          onChange={handlePaymentMethodChange}
          disabled={disabled}
        />
        {isCheque && (
          <ControlledNumberField
            control={control}
            name="chequeNumber"
            disableDecimals
            label={t('loan.payment.productRefundDialog.chequeNumber')}
            disabled={disabled}
          />
        )}
        <ControlledTextField
          control={control}
          name="note"
          label={t('loan.payment.productRefundDialog.note')}
          span="full"
          disabled={disabled}
        />
      </FieldGrid>

      <Banner severity="info">{t('loan.payment.productRefundDialog.info')}</Banner>
    </Stack>
  )
}

/**
 * Collects one product refund and resolves with the calendar recomputed with it. The refund is
 * Accounting's, not Collection's: there is no before/after preview, the calendar is computed
 * once on Confirm, and the dialog stays open with the error if the backend refuses it.
 */
export const useLoanCalendarProductRefundDialog = (
  initialSignedSimulation: SignedCalendarSimulation,
  isLockedByOther: boolean,
) => {
  const { t } = useTranslation()
  const { loan } = initialSignedSimulation.simulation.fullLoan
  const schema = useProductRefundSchema(loan.activationOn)
  const { mutateAsync: computeAction } = useComputeCalendarAction(loan.id, loan.financingCompanyId)
  const ensureRefundableProducts = useEnsureRefundableProducts(loan.id, loan.financingCompanyId)

  const onSubmit = useCallback(
    (values: ProductRefundSchemaType): Promise<SignedCalendarSimulation> =>
      computeAction({
        ...initialSignedSimulation,
        action: {
          actionType: ECalendarSimulationAction.AddProductRefund,
          effectiveDate: values.effectiveDate,
          productIds: values.productIds,
          amount: values.amount,
          paymentMethodId: values.paymentMethodId,
          chequeNumber: values.paymentMethodId === EPaymentMethod.Cheque ? values.chequeNumber : null,
          note: values.note || null,
        },
      }),
    [computeAction, initialSignedSimulation],
  )

  return useFormDialog<ProductRefundSchemaType, void, SignedCalendarSimulation>({
    icon: Undo2,
    title: t('loan.payment.productRefundDialog.title'),
    confirmLabel: t('common.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    schema,
    // The refund is received now, on the first product that can take it, unless the agent says otherwise.
    // Awaiting the list here keeps the dialog on its skeleton until the preselection is known.
    defaultValues: async () => ({
      productIds: (await ensureRefundableProducts()).slice(0, 1).map((product) => product.productId),
      effectiveDate: getToday(),
    }),
    // Confirming stages a pending change on the page, it saves nothing — the page says so.
    successMessage: false,
    onSubmit,
    confirmDisabled: () => isLockedByOther,
    confirmTooltip: () => (isLockedByOther ? t('common.lockTooltip.lockedByOther') : null),
    content: ({ form }) => (
      <LoanCalendarProductRefundBody
        form={form}
        initialSignedSimulation={initialSignedSimulation}
        disabled={isLockedByOther}
      />
    ),
  })
}
